import { useContext, useEffect, useState } from 'react';

import {
  Modal,
  Input,
  Form,
  Button,
  Checkbox,
  Whisper,
  Tooltip,
  toaster,
  Message,
  Loader,
  Grid,
  Row,
  Col,
  SelectPicker,
} from 'rsuite';

import LegacyQuestionCircleIcon from "@rsuite/icons/legacy/QuestionCircle";
import SignatureCanvas from 'react-signature-canvas';
import { gql } from 'apollo-boost';
import { useApolloClient } from '@apollo/client';
import { ApplicationContext } from 'shared';
import { isMobileOnly } from 'react-device-detect';
import { useGetToken } from 'lib/auth';
import { hasMonerisIntegration, hasStripeIntegration } from 'lib/env';
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { useViewport } from 'shared/ViewportProvider';

interface ISignature {
  show: boolean,
  customer?: any,
  onHide: () => void,
  handleSave?: () => void,
  saving?: boolean,
  onApproveDocument: (document: any) => void,
  document: any,
  signatureType: string,
  tenantId?: string,
  tenantContactId?: string
}

const ClientRepresentativeSignature = ({
  show,
  customer,
  document,
  saving,
  onHide,
  onApproveDocument,
  handleSave,
  signatureType,
  tenantId,
  tenantContactId
}: ISignature) => {
  const client = useApolloClient();
  let sigCanvas: SignatureCanvas | null = null;
  const token: any = useGetToken(true);
  const elements = useElements();
  const stripe: any = useStripe();
  const { state } = useViewport();
  const { showError } = useContext(ApplicationContext);
  const [savingCard, setSavingCard] = useState(false);
  const [contractSigned, setContractSigned] = useState(false);
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [comments, setComments] = useState('');
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [currentDocument, setCurrentDocument] = useState<any>(undefined);
  const [loading, setLoading] = useState(false);
  const [formCreditCard, setFormCreditCard] = useState('');
  const [formMonth, setFormMonth] = useState('');
  const [formYear, setFormYear] = useState('');
  const [formCvd, setFormCvd] = useState('');
  const [billingInformationProvided, setBillingInformationProvided] = useState(false);
  const [years, setYears] = useState(new Array(10)
    .fill(0)
    .map((a: any, index: number) => new Date().getFullYear() + index)
    .map((year: number) => ({ value: year.toString().substr(2, 2), label: year }))
  );
  const IS_GREYROCK = token.data.tenant === 'greyrock';

  useEffect(() => {
    setCurrentDocument(document);
    if (customer) {
      setSelectedEmails(customer?.email?.split(';').filter((e: string) => e));
    }
  }, [document, customer]);

  useEffect(() => {

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement('card');

    if (cardElement) {
      cardElement.on('change', (e: any) => {
        setBillingInformationProvided(e.complete);
      });
    }
  }, [show]);

  const handleEmailSelect = (val: any) => {
    if (selectedEmails.includes(val)) {
      const tmp = [...selectedEmails];
      tmp.splice(selectedEmails.indexOf(val), 1);
      // console.log(tmp);
      setSelectedEmails(tmp);
    } else {
      setSelectedEmails([...selectedEmails, val]);
    }
  }

  const handleCreateStripeCard = async () => {
    setSavingCard(true);
    const customerId = token.sub;

    if (!stripe || !elements || !billingInformationProvided) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      setSavingCard(false);
      return;
    }

    const cardElement = elements.getElement('card');
    let response: any = {};

    try {
      response = await client.mutate({
        mutation: gql`mutation upsertStripeCustomer($id: ID!) {
          upsertStripeCustomer(id: $id) {
            success
            code
            message
            result
          }
        }`, variables: { id: customerId }
      });
      toaster.push(<Message type={response.data.upsertStripeCustomer.success ? 'success' : 'error'} showIcon closable>{response.data.upsertStripeCustomer.message}</Message>)
    } catch (err: any) {
      toaster.push(<Message type='error' showIcon closable>{err.message}</Message>);
      setSavingCard(false);
      return;
    }

    if (cardElement) {
      try {
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          metadata: {
            notes: 'Customer provided'
          }
        });

        if (result.error) {
          toaster.push(<Message type='error' showIcon closable>{result.error.message}</Message>);
        } else {
          const binding: any = await client.mutate({
            mutation: gql`mutation attachStripePayment($paymentId: String!, $customerId: ID!, $stripeCustomerId: String!, $isDefault: Boolean, $source: String) {
              attachStripePayment(paymentId: $paymentId, customerId: $customerId, stripeCustomerId: $stripeCustomerId, isDefault: $isDefault, source: $source) {
                success
                code
                message
                result
              }
            }`, variables: { 
              paymentId: result.paymentMethod.id, 
              customerId: customerId, 
              stripeCustomerId: response.data.upsertStripeCustomer.result.stripeCustomerId, 
              isDefault: true,
              source: 'portal' 
            }
          });

          toaster.push(<Message type={binding.data.attachStripePayment.success ? 'success' : 'error'} showIcon closable>{binding.data.attachStripePayment.message}</Message>);
        }
      } catch (err: any) {
        toaster.push(<Message type='error' showIcon closable>{err.message}</Message>)
      } finally {
        setSavingCard(false);
      }
    }
  }

  const handleApproveDocument = async (signature: string, fullName: string, comments: string) => {
    try {
      setLoading(true);
      let response: any | undefined = undefined;

      if (customer) {
        // this is the representative signing
        response = await client.mutate({
          mutation: gql`
            mutation approveDocument(
              $guid: ID!
              $signature: String
              $fullName: String
              $comments: String
              $type: String!
              $emails: [String]
              $jobTitle: String
            ) {
              approveDocument(
                guid: $guid
                signature: $signature
                fullName: $fullName,
                comments: $comments
                type: $type,
                emails: $emails,
                jobTitle: $jobTitle
              ) {
                success
                code
                message
              }
            }
          `,
          variables: { guid: document.guid, signature, fullName, comments, type: 'representative', emails: selectedEmails }
        });
      } else {
        // this is the customer signing
        response = await client.mutate({
          mutation: gql`
            mutation approveDocument(
              $guid: ID!
              $signature: String
              $fullName: String,
              $comments: String
              $type: String!
              $jobTitle: String
              $tenantId: String
              $tenantContactId: String
            ) {
              approveDocument(
                guid: $guid
                signature: $signature
                fullName: $fullName,
                comments: $comments
                type: $type,
                jobTitle: $jobTitle
                tenantId: $tenantId
                tenantContactId: $tenantContactId
              ) {
                success
                code
                message
              }
            }
          `,
          variables: { guid: currentDocument.guid, signature, fullName, comments, type: signatureType, jobTitle, tenantId, tenantContactId }
        });
      }

      if (response.data.approveDocument.success) {
        onApproveDocument({
          ...currentDocument,
          approvalStatus: 'approved',
          signature,
          fullName,
          comments
        });
        toaster.push(
          <Message type="success" showIcon closable>{response.data.approveDocument.message}</Message>
        );
      } else {
        showError(response.data.approveDocument.message);
      }
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={show} size={isMobileOnly ? 'xs' : 'sm'} onClose={onHide}>
      <Modal.Header><Modal.Title>Approve &amp; Sign</Modal.Title></Modal.Header>
      <Modal.Body>
        {(customer && handleSave) &&
          <Message type='info' className={'mb-12'}>
            Make sure to save all changes before signing the document. Signed documents emailed to clients will contain saved details.<br />
            {saving ? <Loader /> : <a href="#" onClick={(e: any) => { e.preventDefault(); handleSave(); }}>Save Now</a>}
          </Message>
        }

        {(hasStripeIntegration(state.companies) && !(IS_GREYROCK && (document?.entryType || '').includes('commercial'))) && <div className='mb-12'>
          <Message type="info" className='mb-12'><div>Skip billing information if you prefer to confirm over the phone.</div></Message>
          <div>
            <label>Billing:</label><br />
            <CardElement options={{ hidePostalCode: true }} />
          </div>
        </div>}

        {hasMonerisIntegration(state.companies) && <div className='mb-12'>
          <Message type="info" className='mb-12'><div>Skip billing information if you prefer to confirm over the phone.</div></Message>
          <label>Billing:</label><br />
          <Grid fluid className='p-0'>
            <Row className={'mb-12'}>
              <Col md={10}>
                Credit card:<br />
                <Input value={formCreditCard} onChange={setFormCreditCard} tabIndex={0} />
              </Col>
              <Col md={5}>
                Month:<br />
                <SelectPicker
                  cleanable={false}
                  value={formMonth}
                  searchable={false}
                  block
                  onChange={(val: any) => {
                    setFormMonth(val);
                  }}
                  data={[
                    { value: '01', label: '01 - Jan' },
                    { value: '02', label: '02 - Feb' },
                    { value: '03', label: '03 - Mar' },
                    { value: '04', label: '04 - Apr' },
                    { value: '05', label: '05 - May' },
                    { value: '06', label: '06 - Jun' },
                    { value: '07', label: '07 - Jul' },
                    { value: '08', label: '08 - Aug' },
                    { value: '09', label: '09 - Sep' },
                    { value: '10', label: '10 - Oct' },
                    { value: '11', label: '11 - Nov' },
                    { value: '12', label: '12 - Dec' }
                  ]}
                />
              </Col>
              <Col md={5}>
                Year:
                <SelectPicker
                  cleanable={false}
                  searchable={false}
                  block
                  value={formYear}
                  onChange={(val: any) => {
                    setFormYear(val);
                  }}
                  data={years}
                />
              </Col>
              <Col md={4}>
                CVD:<br />
                <Input value={formCvd} onChange={setFormCvd} tabIndex={0} />
              </Col>
            </Row>
          </Grid>
        </div>}

        <div className="mb-5">
          <p>All fields are required to completed approval.</p>
        </div>
        <div className="mb-5">
          <label>Full name:</label>
          <Input
            disabled={loading}
            type="text"
            value={fullName}
            onChange={setFullName}
          />
        </div>
        {(IS_GREYROCK && (document?.entryType || '').includes('commercial')) &&
          <div className="mb-5">
            <label>Title:</label>
            <Input
              disabled={loading}
              type="text"
              value={jobTitle}
              onChange={setJobTitle}
            />
          </div>
        }
        <div className="mb-5">
          <label>Comments:</label>
          <Input
            disabled={loading}
            as="textarea"
            rows={2}
            style={{ width: '100%' }}
            value={comments}
            onChange={setComments}
          />
        </div>
        <div>
          <label>Signature:</label>
          <SignatureCanvas
            ref={(ref) => { sigCanvas = ref }}
            penColor='blue'
            canvasProps={{ width: 555, height: 200, className: 'sigCanvas', style: { border: 'solid 1px #ccc' } }}
            onEnd={() => {
              setContractSigned(true);
            }}
          />
          <Form.HelpText>Draw your signature in the provided space. Any signatures/initals found as not legible will be removed and a new signature will be required.</Form.HelpText>
        </div>
        {customer?.email &&
          <div className="mt-5">
            <label>
              Email following clients signed documents:{' '}
              <Whisper placement="top" speaker={<Tooltip>Template: Document Representative Approved</Tooltip>}><LegacyQuestionCircleIcon /></Whisper>
            </label>
            {customer?.email.split(';').filter((e: string) => e).map((e: string, index: number) =>
              <Checkbox checked={selectedEmails.indexOf(e) > -1} value={e} key={`${e}-${index}`} onChange={handleEmailSelect}>{e}</Checkbox>
            )}
          </div>
        }
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => {
          if (sigCanvas) {
            setContractSigned(false);
            sigCanvas.clear();
          }
        }} appearance="subtle" size="sm">Clear Signature</Button>
        <Button
          loading={loading || savingCard}
          disabled={!contractSigned || fullName.length === 0}
          onClick={async () => {
            if (!sigCanvas?.isEmpty()) {
              const canvas = sigCanvas?.getTrimmedCanvas();
              await handleCreateStripeCard();
              await handleApproveDocument((canvas as any).toDataURL(), fullName, comments);
            }
          }} appearance="primary" size="sm">Finish</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ClientRepresentativeSignature;
